import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { Display, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { ALIGN_CONTENT, FLEX_CONTAINER_ROW, JUSTIFY_CONTENT, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { ensureLoaded } from "@cervval.js/common/src/tools/object.tools";
import { CjsInput, InputOptions } from "../../components/inputs/input";
import { CjsEditor, EditorOptions, defaultEditorOptions } from "../editor";

ensureLoaded(CjsInput);

export interface RangeEditorOptions extends EditorOptions {
    /**
     * If set to true, display an input after the range input to display / set value of input
     */
    displayValue?: boolean;
}

const defaultRangeEditorOptions: Partial<RangeEditorOptions> = {
    ...defaultEditorOptions
};

/**
 * Class representing a component with a range editor
 */
@CustomElement({
    name: "cjs-input-range-editor",
    template: require("./inputRangeEditor.html"),
    style: require("./inputRangeEditor.scss"),
    elementCssClasses: ["cjs-editor", FLEX_CONTAINER_ROW, W_100, JUSTIFY_CONTENT.EVENLY, ALIGN_CONTENT.CENTER]
})
export class CjsInputRangeEditor extends CjsEditor<number> {

    /** Attributes options of editor */
    @TypedWebComponentOptions(defaultRangeEditorOptions)
    public readonly options!: WebComponentOptions<RangeEditorOptions>;

    @TypedHtmlElements()
    public readonly components!: {
        inputRange: CjsInput<number>,
        valueDisplay: CjsInput<number>
    };

    /** Attributes options of input of InputTextEditor */
    public get inputOptions(): WebComponentOptions<InputOptions<number>> {
        return this.getInput().options;
    }

    constructor(options?: Partial<RangeEditorOptions>, inputOptions?: Partial<InputOptions<number>>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
        this.inputOptions.setWithoutRefresh(inputOptions);
    }

    /**
     * @inheritdoc
     */
    public override init(): void {
        super.init();
        this.getInput().changeEvent.on((evt: Event) => this._onInputValueChange(evt));
    }

    /** @inheritdoc */
    public override refresh(): void {
        super.refresh();
        // We only enableFormControl on multiple value state, otherwise display isn't pretty
        this.inputOptions.setWithoutRefresh({ value: this._value, enableFormControl: this.options.multipleValuesState });
        this._refreshValueDisplay();
        // Refresh input
        this.getInput().refresh();
        this.components.valueDisplay.refresh();
    }

    /**
     * Refresh value display from options
     */
    private _refreshValueDisplay(): void {
        toggleVisible(this.components.valueDisplay, this.options.displayValue, Display.INHERIT);
        if (this.options.displayValue) {
            this.components.inputRange.inputEvent.on(this._inputDisplayCallback);
            this._inputDisplayCallback();
            this.components.valueDisplay.changeEvent.on(this._propagateValue);
        } else {
            this.components.inputRange.inputEvent.off(this._inputDisplayCallback);
            this.components.valueDisplay.changeEvent.off(this._propagateValue);
        }
    }

    /**
     * Callback to retrieve value from input and display it
     */
    private _inputDisplayCallback: () => void = () => {
        const val = this._getValueFromInputComponent();
        this.components.valueDisplay.options.setAndRefreshIfChanged({ value: val });
    };

    /**
     * Callback to propagate valueDisplay on change to inputRange
     */
    private _propagateValue: () => void = () => {
        if (this.inputOptions.setAndRefreshIfChanged({ value: this.components.valueDisplay.value })) {
            // Propagate value change on this._value
            this._onInputValueChange();
            // Value can be different (capped or change to nearest multiple of input.step), so we propagate the input value to the display
            this._inputDisplayCallback();
        }
    };

    /** @inheritdoc */
    public getInput(): CjsInput<number> {
        return this.components.inputRange;
    }

    /** @inheritdoc */
    protected _getValueFromInputComponent(): number | undefined {
        return this.getInput().value;
    }
    /** @inheritdoc */
    protected _refreshInputValue(): void {
        this.inputOptions.setAndRefreshIfChanged({ value: this._value });
    }
    /** @inheritdoc */
    protected _refreshDisabled(): void {
        this.inputOptions.setWithoutRefresh({ disabled: this.disabled });
        this.components.valueDisplay.options.setWithoutRefresh({ disabled: this.disabled });
    }

    /** Set formCheckInput as validation form element */
    public override getValidationFormElement(): HTMLInputElement | HTMLTextAreaElement {
        return this.getInput().input;
    }
}